// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - SDKAreaMessaging
export class AreaMessaging {
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(dataCore) {
        this.dataCore = dataCore;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Read
    /**
     * Lists chat threads in the specified workspace for the current user based on the session.
     * @param workspaceId - The ID of the workspace.
     * @param limit - Optional limit on the number of threads to return.
     * @param offset - Optional offset for pagination.
     * @returns An array of ChatThread objects.
     */
    async listChatThreads(workspaceId, limit, offset) {
        return this.dataCore.listThreads(workspaceId, limit, offset);
    }
    /**
     * Fetches a specific chat thread by its ID. This method retrieves the thread details including its messages and is the only way to get thread content.
     * @param workspaceId - The ID of the workspace.
     * @param thread - The thread object to update.
     * @returns A new copy of the The ChatThread object with filled-in messages.
     */
    async getChatThreadMessages(workspaceId, thread) {
        return this.dataCore.getChatThreadMessages(workspaceId, thread);
    }
    /**
     * Fetches a specific chat thread by its ID. This method retrieves the thread details including its messages and is the only way to get thread content.
     * @param workspaceId - The ID of the workspace.
     * @param threadId - The ID of the thread.
     * @returns A new copy of the The ChatThread object with filled-in messages.
     */
    async getChatThreadMessagesByThreadId(workspaceId, threadId) {
        return this.dataCore.getChatThreadMessagesByThreadId(workspaceId, threadId);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Write
    /**
     * Creates a new chat thread in the specified workspace associated with the current user based on the session.
     * @param workspaceId - The ID of the workspace.
     * @param title - Optional title for the thread (defaults to "Untitled").
     * @returns The created ChatThread.
     */
    async createChatThread(workspaceId, title) {
        return this.dataCore.createThread(workspaceId, title);
    }
    /**
     * Updates the title of a specific chat thread.
     * @param workspaceId - The ID of the workspace.
     * @param threadId - The ID of the thread to update.
     * @param newTitle - The new title for the thread.
     * @returns The updated ChatThread.
     */
    async updateChatThreadTitle(workspaceId, threadId, newTitle) {
        return this.dataCore.updateThreadTitle(workspaceId, threadId, newTitle);
    }
    /**
     * Creates a new message in the specific chat thread.
     * @param workspaceId - The ID of the workspace.
     * @param threadId - The ID of the thread.
     * @param messageInput - The message details including payload, senderType, and optional persona.
     * @returns The created ChatThreadMessage.
     */
    async createChatThreadMessage(workspaceId, threadId, messageInput) {
        return this.dataCore.createMessage(workspaceId, threadId, messageInput);
    }
    async scoreChatThreadMessage(workspaceId, threadId, scores, tags) {
        await this.dataCore.scoreChatThreadMessage(workspaceId, threadId, scores, tags);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Delete
    /**
     * Deletes a specific chat thread and all its messages.
     * @param workspaceId - The ID of the workspace.
     * @param threadId - The ID of the thread to delete.
     */
    async deleteChatThread(workspaceId, threadId) {
        return this.dataCore.deleteThread(workspaceId, threadId);
    }
}
